import { usePageMeta, useFAQSchema } from '@/hooks/useSEO';
import {
  PageHero, TrustBox, DataTable, FrameworkCTA, SectionH2, ILink, useBreadcrumbSchema,
} from './_shared';

const FAQS = [
  { q: 'Can a person on a Commercial Business Visit Visa perform physical work in KSA?', a: 'No. Commercial Visit Visas prohibit hands-on physical labor or local salary collection. Engaging in operational work on a commercial visit visa risks deportation and corporate penalties.' },
  { q: 'Does Premium Residency require a Saudi national sponsor?', a: 'No. Premium Residency gives expatriates full independence from local employer sponsorship.' },
];

const PATH = '/services/saudi-business-visit-visa/';

/* SPOKE 2 — Business Visit & Premium Residency */
export default function SaudiBusinessVisitPremiumPage() {
  usePageMeta(
    'Saudi Arabia Commercial Business Visit Visa & Premium Residency Guide 2026 | Zafco Services',
    'Complete 2026 guide to Saudi Commercial Business Visit Visas, MOFA Letter of Invitation (LOI) workflow, Commercial vs. Working Visit compliance, and Premium Residency channels.',
    { canonical: PATH },
  );
  useFAQSchema(FAQS);
  useBreadcrumbSchema([
    { name: 'Home', path: '/' },
    { name: 'Saudi Visa Framework 2026', path: '/saudi-visa-framework-2026/' },
    { name: 'Business Visit & Premium Residency', path: PATH },
  ]);

  return (
    <article data-testid="saudi-business-premium-root" className="bg-white">
      <PageHero
        mid="Saudi Visa Framework 2026"
        crumb="Business Visit & Premium Residency"
        title="Saudi Arabia Commercial Business Visit Visas & Premium Residency Framework (2026)"
      />

      <div className="container mx-auto px-6 max-w-5xl py-12">
        <TrustBox
          author="Zafco Enterprise Mobility & Business Visa Division"
          reviewer="Senior Saudi Regulatory & Corporate Immigration Advisory Team"
          standard="Saudi Ministry of Foreign Affairs (MOFA), Ministry of Investment (MISA), & Premium Residency Center (PRC)"
        />

        <section id="commercial-vs-working-visit">
          <SectionH2 id="commercial-vs-work-h">1. Commercial Boundary: Commercial Visit vs. Work Visas</SectionH2>
          <p className="text-slate-700 leading-relaxed mb-4">
            A crucial compliance aspect in Saudi Arabia's mobility regime is distinguishing between business meetings and hands-on operational work. For long-term employment deployment, refer to our detailed{' '}
            <ILink to="/services/saudi-work-visa-qvp-attestation/" testId="biz-link-qvp">Saudi Work Visa & QVP Guide</ILink>, as well as the overall{' '}
            <ILink to="/saudi-visa-framework-2026/" testId="biz-link-hub">Saudi Visa Framework 2026</ILink>. For document checklists and single-entry application walkthroughs, our{' '}
            <ILink to="/saudi-arabia/business-visa" testId="biz-link-old-business">Saudi Business Visit Visa for Indians guide</ILink> covers the applicant-side process step by step.
          </p>
          <DataTable
            testId="commercial-vs-work-table"
            headers={['Parameter', 'Commercial Business Visit Visa', 'Work Visa (Employment)']}
            rows={[
              [<strong>Permitted Scope</strong>, 'B2B meetings, contract negotiation, vendor audits, strategy sessions.', 'Gainful employment, operational installation, hands-on labor.'],
              [<strong>In-Country Remuneration</strong>, 'Strictly Prohibited (Paid by home country entity).', 'Mandatory (Paid via Saudi entity through Mudad WPP).'],
              [<strong>Local Status</strong>, 'Visitor Status (No Iqama issued).', 'Resident Status (Iqama issued via Jawazat).'],
              [<strong>Maximum Validity</strong>, 'Up to 1 Year (Multiple Entry, 90-day stay per visit).', 'Renewable Annual Residence Permit.'],
            ]}
          />
        </section>

        <section id="mofa-loi-workflow">
          <SectionH2 id="mofa-loi-h">2. MOFA Letter of Invitation (LOI) & MISA Authorizations</SectionH2>
          <p className="text-slate-700 leading-relaxed mb-4">
            To obtain a Commercial Business Visit Visa, the host Saudi company must issue a formal electronic Letter of Invitation (LOI) authenticated through MOFA or MISA.
          </p>
          <h3 className="text-xl font-bold text-[#000080] font-syne mt-6 mb-3">Key Submission Requirements for Business Visit Stamping:</h3>
          <ul className="list-disc pl-6 space-y-3 text-slate-700 leading-relaxed">
            <li><strong>MOFA Official Invitation Letter:</strong> Issued by the sponsoring Saudi entity with designated profession title (e.g., Business Consultant, Regional Director, Technical Auditor).</li>
            <li><strong>Host CR & MISA License:</strong> Commercial Registration copy of the host entity; MISA License required for foreign-owned entities.</li>
            <li><strong>Employer Recommendation Letter:</strong> Issued by the foreign employer on company letterhead, attested by the local Chamber of Commerce.</li>
          </ul>
        </section>

        <section id="tourist-eVisa" className="mt-10">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 text-sm text-slate-700 leading-relaxed">
            <strong className="text-[#000080]">Tourist eVisa Note:</strong> Leisure travel, cultural events, and Umrah (excluding Hajj) fall under the sponsor-free Tourist eVisa channel — strictly no paid work permitted. See the dedicated{' '}
            <ILink to="/saudi-arabia/tourist-visa" testId="biz-link-tourist">Saudi Tourist Visa rules for Indians</ILink>.
          </div>
        </section>

        <section id="premium-residency">
          <SectionH2 id="premium-residency-h">3. Saudi Arabia Premium Residency Framework (PRC 2026)</SectionH2>
          <p className="text-slate-700 leading-relaxed">
            Managed by the Premium Residency Center (PRC), this pathway offers sponsor-free long-term residency options for high-net-worth individuals, investors, and specialized talents.
          </p>
          <DataTable
            testId="premium-residency-table"
            headers={['Pathway', 'Target Profile', 'Core Eligibility Criteria']}
            rows={[
              [<strong>Specialized Talent</strong>, 'Healthcare, Executive Leadership, Scientific Researchers', 'Specialized qualification, minimum monthly salary thresholds (SAR 35,000–80,000), valid employment contract.'],
              [<strong>Investor Residency</strong>, 'Direct Commercial Investors', 'Direct investment of SAR 7 Million into KSA business operations within 2 years + 10 local jobs created.'],
              [<strong>Entrepreneur Residency</strong>, 'Startup Founders & Innovators', 'MISA-approved incubator sponsorship; minimum SAR 400k (Category 1) or SAR 15M investment (Category 2).'],
              [<strong>Real Estate Owner</strong>, 'Property Buyers', 'Unencumbered title deed to Saudi residential property worth minimum SAR 4 Million.'],
            ]}
          />
        </section>

        <FrameworkCTA />

        <section id="faqs">
          <SectionH2 id="faqs-h">Frequently Asked Questions</SectionH2>
          {FAQS.map((f) => (
            <div key={f.q} className="mb-5">
              <h3 className="text-base font-bold text-[#000080] mb-1.5">{f.q}</h3>
              <p className="text-slate-700 leading-relaxed text-sm">{f.a}</p>
            </div>
          ))}
        </section>
      </div>
    </article>
  );
}
